import { IconLinkOff } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { api, ApiError, type LinkOpen } from "../api";
import { Footer } from "../components/Footer";
import { Spinner } from "../components/ui";
import type { Key } from "../i18n";
import { navigate } from "../router";
import { useSession } from "../session";

const FAIL_KEYS: Record<string, Key> = { link_unknown: "linkUnknown", link_expired: "linkExpired", link_revoked: "linkRevoked" };

/** `/t/:token`: opens the signed link, then goes where it points; a dead link shows a friendly page. */
export function LinkOpenerPage({ token, onSignInEmail }: { token: string; onSignInEmail: (email: string) => void }) {
  const { refresh, t } = useSession();
  const [failure, setFailure] = useState<Key | null>(null);
  const [placeholder, setPlaceholder] = useState<LinkOpen["kind"] | null>(null);
  const tried = useRef<string | null>(null);

  useEffect(() => {
    if (tried.current === token) return;
    tried.current = token;
    api
      .openLink(token)
      .then(async (res) => {
        if (res.hasIdentity) await refresh();
        if (res.kind === "trip") navigate(`/trips/${res.subjectId}`, { replace: true });
        else if (res.kind === "proposal") navigate(`/p/${res.subjectId}`, { replace: true });
        else if (res.kind === "payment") navigate(`/trips/${res.subjectId}#pay`, { replace: true });
        else if (res.kind === "residents") navigate("/residents", { replace: true });
        else if (res.kind === "sign_in") {
          if (res.email) onSignInEmail(res.email);
          navigate("/signin", { replace: true });
        } else setPlaceholder(res.kind);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && FAIL_KEYS[err.code]) setFailure(FAIL_KEYS[err.code] ?? "linkUnknown");
        else if (err instanceof ApiError && err.status === 410) setFailure("linkExpired");
        else setFailure("somethingWrong");
      });
  }, [token, refresh, onSignInEmail]);

  return (
    <div className="page page-center">
      <main className="centered">
        {failure ? (
          <div className="card signin-card">
            <IconLinkOff size={36} stroke={1.25} className="muted" />
            <h1>{t(failure)}</h1>
            <p className="muted">{t("linkBody")}</p>
            <a href="/signin" className="button button-primary button-block">
              {t("signIn")}
            </a>
          </div>
        ) : placeholder ? (
          <div className="card signin-card">
            <span className="eyebrow">{t("comingSoon")}</span>
            <h1>{t("helpSoon")}</h1>
            <a href="/" className="button button-primary button-block">
              {t("goHome")}
            </a>
          </div>
        ) : (
          <Spinner label={t("openingLink")} />
        )}
      </main>
      <Footer />
    </div>
  );
}
